import type { AnalyticsPort } from '#modules/analytics';
import type { User } from '#types/user';

import * as Datadog from './datadog';
import * as PostHog from './posthog';

/**
 * Vida's own `AnalyticsPort` implementation — PostHog for product analytics, Datadog for
 * RUM and Logs.
 *
 * **This fan-out is the thing that belongs in an adapter.** It used to live in
 * `analytics/utils/analytics.ts`, which meant every file reaching the analytics facade
 * also reached both vendor SDKs. A host injecting its own port fans out however it already
 * does — VidaReactNative adds Braze and Snowplow, login has neither — so one call becoming
 * exactly "a PostHog capture plus a Datadog RUM view" is a Vida-standalone decision, not a
 * property of the port.
 *
 * Each member below is a bare delegation with no try/catch. That is not an oversight in
 * two directions: every function in `./posthog` and `./datadog` already swallows and logs
 * its own errors, and `analytics/utils/analytics.ts` guards the outer call as well. A third
 * layer here would only make a real failure harder to locate.
 */
export const vidaAnalytics: AnalyticsPort = {
  identifyUser: (user: User) => {
    PostHog.initializeUser(user);
    void Datadog.initializeUser(user);
  },

  resetUser: () => {
    PostHog.resetUser();
    void Datadog.resetUser();
  },

  trackEvent: (eventName, properties) => {
    // PostHog only — Datadog records interactions through RUM automatically.
    PostHog.trackEvent(eventName, properties);
  },

  /**
   * Safe here, and only here. `Datadog.trackView` is `DdRum.startView`, which replaces the
   * active RUM view — correct when the view tree is ours (standalone, `trackScreens: true`),
   * destructive inside a host. That is the whole reason `AnalyticsPort` grew `trackStep`.
   */
  trackScreen: (screenName, properties) => {
    PostHog.trackScreen(screenName, properties);
    // Key and name are the same value: RUM's view key is what groups a view's
    // resources/errors, and the pathname is already unique per screen.
    void Datadog.trackView(screenName, screenName, properties);
  },

  // `trackStep` is intentionally absent, for the same reason as `trackAppOpen` below: we own
  // the view tree standalone, so `trackScreen` above already reports every navigation as a
  // `$pageview` plus a RUM view. Implementing both would count one navigation twice. The
  // member exists for hosts, which is exactly where the view primitive is unavailable.

  trackError: (error, context) => {
    const errorMessage = error instanceof Error ? error.message : error;
    PostHog.trackEvent('error', { error: errorMessage, ...context });
    void Datadog.trackError(error, context);
  },

  /**
   * Datadog Logs only, deliberately — this is the pair that must not inflate PostHog
   * event volume. The `__DEV__` console mirror lives in the facade, so it holds for a host
   * port too.
   */
  logError: (message, context) => {
    void Datadog.logError(message, context);
  },

  logInfo: (message, context) => {
    void Datadog.logInfo(message, context);
  },

  setUserProperties: (properties) => {
    PostHog.setUserProperties(properties);
    void Datadog.setUserProperties(properties);
  },

  /** Datadog-only by definition — the whole point of the member. */
  setDatadogGlobalContext: (properties) => {
    void Datadog.setUserProperties(properties);
  },

  removeUserProperty: (key) => {
    PostHog.removeUserProperty(key);
    void Datadog.removeUserProperty(key);
  },

  // Flags are PostHog's. `trackAppOpen` is intentionally absent: its implementation has
  // been an empty `try {}` since the Snowplow path was dropped, so leaving it unimplemented
  // makes the facade no-op instead of dressing up a no-op as a delegation.
  getFeatureFlag: (key) => PostHog.getFeatureFlag(key),
  isFeatureFlagEnabled: (key) => PostHog.isFeatureFlagEnabled(key),
  onFeatureFlags: (callback) => PostHog.onFeatureFlags(callback),
};
